import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { type Result, success } from '@orus.eu/result'
import type { LocalizableMessage, TranslationKey } from '@orus.eu/translations'

import {
  AbstractDimension,
  type DimensionMcpAdapter,
  type DimensionOptions,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'

export const DEFAULT_TEXT_DIMENSION_MAX_LENGTH = 255

export class TextDimension<NAME extends string> extends AbstractDimension<NAME, string> {
  readonly type = 'text'
  constructor(options: DimensionOptions<NAME, string>) {
    const finalOptions = { ...options, maxLength: options.maxLength ?? DEFAULT_TEXT_DIMENSION_MAX_LENGTH }
    super(finalOptions)
  }

  override validateData(value: LooselyTypedValue): Result<string, DimensionValidationProblem> {
    if (typeof value !== 'string') {
      return dimensionValidationFailure(`Field ${this.name} is not a string`)
    }
    return success(value.trim())
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<string> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.string(valueDocumentation))
    },
    convertStateValueToTrackingValue: (stateValue: string | null | undefined): string | null => {
      return stateValue ?? null
    },
  }

  override formatToString(value: string): string | undefined {
    return value
  }

  override readonly mcpAdapter: DimensionMcpAdapter<string> = {
    parseValueFromMcp(mcpValueInput: string): Result<string, string> {
      return success(mcpValueInput)
    },
    formattingInstructions: `For this tool, you must specify a valid text.`,
  }
}

export class LocalizableTextDimension<NAME extends string, Key extends TranslationKey> extends AbstractDimension<
  NAME,
  LocalizableMessage<Key>
> {
  readonly type = 'localizable-text'

  override formatToString(_value: LocalizableMessage<Key>): string | undefined {
    return undefined
  }
}
